import React, { useEffect, useState } from "react";
import {
  TextField,
  Grid,
  Box,
  Card,
  CardContent,
  CardActions,
  Container,
  CircularProgress,
  Slide,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_COMPANY_PROFILE,
  UPDATE_COMPANY_PROFILE,
} from "../../../apis/settingsApis/company-profile/companyProfileApis";
import { LoadingButton } from "@mui/lab";
import useAppStore from "../../../stores";
import { FrameworkKey } from "../../../utils/enums";
import CompanyLogoUpload from "../../../components/CompanyLogoUpload/CompanyLogoUpload";

const validationSchema = Yup.object().shape({
  companyName: Yup.string().required("Company Name is required"),
  companyDomain: Yup.string().required("Company Domain is required"),
  companyWebsite: Yup.string().required("Company Website is required"),
  systemDescription: Yup.string().required("System Description is required"),
  aboutCompany: Yup.string().required("About Company is required"),
});

const CompanyProfiles = ({ setBreadcrumbLists }) => {
  const theme = useTheme();
  const { loading, error, data, refetch } = useQuery(GET_COMPANY_PROFILE);
  const [updateCompanyProfile] = useMutation(UPDATE_COMPANY_PROFILE);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const frameworks = useAppStore((state) => state.frameworks);
  const isDpdp = frameworks?.some(
    (f) => f?.key === FrameworkKey.DPDP && f?.is_active
  );

  const [initialValues, setInitialValues] = useState({
    companyName: "",
    companyDomain: "",
    companyWebsite: "",
    systemDescription: "",
    aboutCompany: "",
    companyAddress: "",
    isSignificantDataFiduciary: false,
  });

  useEffect(() => {
    setBreadcrumbLists(["Settings", "Company Settings"]);
  }, [setBreadcrumbLists]);

  useEffect(() => {
    if (data && data.getCustomerCompanyProfileDetails) {
      setInitialValues({
        companyName:
          data.getCustomerCompanyProfileDetails.customer_company_name || "",
        companyDomain:
          data.getCustomerCompanyProfileDetails.company_domain || "",
        companyWebsite:
          data.getCustomerCompanyProfileDetails.company_website || "",
        systemDescription:
          data.getCustomerCompanyProfileDetails.company_description || "",
        aboutCompany:
          data.getCustomerCompanyProfileDetails.company_services || "",
        companyAddress:
          data.getCustomerCompanyProfileDetails.company_address || "",
        isSignificantDataFiduciary:
          data.getCustomerCompanyProfileDetails
            .is_significant_data_fiduciary || false,
      });
    }
  }, [data]);

  const TransitionComponent = (props) => {
    return <Slide {...props} direction="left" />;
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      await updateCompanyProfile({
        variables: {
          companyDetails: {
            customer_company_name: values.companyName,
            company_domain: values.companyDomain,
            company_website: values.companyWebsite,
            company_services: values.aboutCompany,
            company_address: values.companyAddress,
            company_description: values.systemDescription,
            is_significant_data_fiduciary: values.isSignificantDataFiduciary,
          },
        },
      });

      await refetch();

      setSnackbarInfo({
        message: "Company details updated successfully!",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    } catch (err) {
      console.error("Error updating company profile:", err);
    }
    setIsLoading(false);
  };

  const handleFetchProfile = () => {
    setIsFetching(true);
    setTimeout(() => {
      setInitialValues((prevValues) => ({
        ...prevValues,
        systemDescription:
          "Solsphere AI is a cybersecurity firm specializing in comprehensive security audits tailored to meet unique client needs. Their core services include Vulnerability Assessment and Penetration Testing (VAPT), Cloud Security, and Code Security. \n \n Client testimonials highlight satisfaction with Solsphere AI's services, particularly in areas like DevOps, cloud, and infrastructure security. Clients have praised the quality of deliverables and the support provided by the team.",
        aboutCompany:
          " Offers tailored security audits, including VAPT, Cloud Security, and Code Security",
      }));
      setIsFetching(false);
    }, 2000);
  };

  if (loading) {
    return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{ textAlign: "center", mt: 8 }}
      >
        <CircularProgress />
      </Container>
    );
  }
  if (error) return <p>Error: {error.message}</p>;

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ touched, errors }) => (
          <Form>
            <Card sx={{ width: "100%" }} elevation={0}>
              <CardContent p={2}>
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2.5}>
                    {/* Company logo: saved on its own, shared with the Trust Center */}
                    <Grid item xs={12}>
                      <CompanyLogoUpload />
                    </Grid>

                    {/* Company Name */}
                    <Grid item xs={12}>
                      <Typography
                        component="label"
                        sx={{
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: theme.customColors?.text?.strong ?? "#374151",
                          mb: 0.75,
                        }}
                      >
                        Company Name *
                      </Typography>
                      <Field
                        as={TextField}
                        name="companyName"
                        fullWidth
                        variant="outlined"
                        placeholder="Company Name"
                        error={
                          touched.companyName && Boolean(errors.companyName)
                        }
                        helperText={<ErrorMessage name="companyName" />}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            "&:hover fieldset": {
                              borderColor: theme.customColors.border.strong,
                            },
                          },
                        }}
                      />
                    </Grid>

                    {/* Company Domain */}
                    {/* <Grid item xs={12} >
                      <Field
                        as={TextField}
                        name="companyDomain"
                        fullWidth
                        variant="outlined"
                        label="Company Domain *"
                        placeholder="Company Domain"
                        InputLabelProps={{ shrink: true }}
                        error={
                          touched.companyDomain && Boolean(errors.companyDomain)
                        }
                        helperText={<ErrorMessage name="companyDomain" />}
                      />
                    </Grid> */}

                    {/* Company Website */}
                    <Grid item xs={12}>
                      <Typography
                        component="label"
                        sx={{
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: theme.customColors?.text?.strong ?? "#374151",
                          mb: 0.75,
                        }}
                      >
                        Company Website *
                      </Typography>
                      <Field
                        as={TextField}
                        name="companyWebsite"
                        fullWidth
                        variant="outlined"
                        placeholder="Company Website"
                        error={
                          touched.companyWebsite &&
                          Boolean(errors.companyWebsite)
                        }
                        helperText={<ErrorMessage name="companyWebsite" />}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            "&:hover fieldset": {
                              borderColor: theme.customColors.border.strong,
                            },
                          },
                        }}
                      />
                    </Grid>

                    {/* System Description */}
                    <Grid item xs={12}>
                      <Typography
                        component="label"
                        sx={{
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: theme.customColors?.text?.strong ?? "#374151",
                          mb: 0.75,
                        }}
                      >
                        System Description *
                      </Typography>
                      <Field
                        as={TextField}
                        name="aboutCompany"
                        fullWidth
                        multiline
                        rows={2}
                        variant="outlined"
                        placeholder="Write about System Description..."
                        error={
                          touched.aboutCompany && Boolean(errors.aboutCompany)
                        }
                        helperText={<ErrorMessage name="aboutCompany" />}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            "&:hover fieldset": {
                              borderColor: theme.customColors.border.strong,
                            },
                          },
                        }}
                      />
                    </Grid>

                    {/* Company Address */}
                    <Grid item xs={12}>
                      <Typography
                        component="label"
                        sx={{
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: theme.customColors?.text?.strong ?? "#374151",
                          mb: 0.75,
                        }}
                      >
                        Company Address
                      </Typography>
                      <Field
                        as={TextField}
                        name="companyAddress"
                        fullWidth
                        multiline
                        rows={2}
                        variant="outlined"
                        placeholder="Building, Street, Locality, City, Country"
                        error={
                          touched.companyAddress &&
                          Boolean(errors.companyAddress)
                        }
                        helperText={<ErrorMessage name="companyAddress" />}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            "&:hover fieldset": {
                              borderColor: theme.customColors.border.strong,
                            },
                          },
                        }}
                      />
                    </Grid>

                    {/* About Company */}
                    <Grid item xs={12}>
                      <Typography
                        component="label"
                        sx={{
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: theme.customColors?.text?.strong ?? "#374151",
                          mb: 0.75,
                        }}
                      >
                        About Company *
                      </Typography>
                      <Field
                        as={TextField}
                        name="systemDescription"
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        placeholder="About Company"
                        error={
                          touched.systemDescription &&
                          Boolean(errors.systemDescription)
                        }
                        helperText={<ErrorMessage name="aboutCompany" />}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            "&:hover fieldset": {
                              borderColor: theme.customColors.border.strong,
                            },
                          },
                        }}
                      />
                    </Grid>

                    {/* Significant Data Fiduciary — DPDP only */}
                    {isDpdp && (
                      <Grid item xs={12}>
                        <Box
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <FormControlLabel
                            sx={{
                              ml: 0,
                              mr: 0.5,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                color:
                                  theme.customColors?.text?.strong ?? "#374151",
                              },
                            }}
                            control={
                              <Field
                                as={Checkbox}
                                type="checkbox"
                                name="isSignificantDataFiduciary"
                                sx={{ py: 0, pl: 0, pr: 1 }}
                              />
                            }
                            label="My company is a Significant Data Fiduciary under the DPDP Act"
                          />
                          <Tooltip
                            arrow
                            title="Tick this only if you have been notified as a Significant Data Fiduciary by the regulatory body (the Data Protection Board / Central Government) under the DPDP Act. If you have not received such a notification, leave it unchecked."
                          >
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: 20,
                                pl: 0.5,
                                color:
                                  theme.customColors?.icons?.primary ??
                                  "#6B7280",
                                cursor: "pointer",
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end", p: 2 }}>
                {/* <Button
                  variant="outlined"
                  onClick={handleFetchProfile}
                  disabled={isFetching}
                >
                  {isFetching ? <CircularProgress size={24} /> : "Fetch Company Profile"}
                </Button> */}
                <LoadingButton
                  type="submit"
                  variant="contained"
                  sx={{ padding: "10px 40px" }}
                  loading={isLoading}
                >
                  Save
                </LoadingButton>
              </CardActions>
            </Card>
          </Form>
        )}
      </Formik>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default CompanyProfiles;
